import { useState, useEffect } from 'react';
import Table from './Table';
import { License } from '../../models/License';
import { Brand, BrandDescriptions } from '../../models/Brand';
import { DeliveryNoteReaderType, DeliveryNoteReaderTypeDescriptions } from '../../models/DeliveryNoteReaderType';
import { createLicense, editLicense, listAllLicenses } from '../../service/LicenseService';
import { LicenseCreateRequest } from '../../models/LicenseCreateRequest';
import { LicenseEditRequest } from '../../models/LicenseEditRequest';
import axios from 'axios';

const btn = {
    primary:   'gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition text-sm font-medium',
    secondary: 'border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition text-sm',
};
const input   = 'w-full px-4 py-2 border border-gray-300 rounded-lg text-sm transition';
const modal   = 'bg-white rounded-2xl shadow-xl p-6 w-full max-w-md';
const overlay = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50';

const LicensesSection = () => {
    const [licenses, setLicenses] = useState([] as License[]);
    const [isLoading, setIsLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
    const [loadingCreate, setLoadingCreate] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });

    const [newLicense, setNewLicense] = useState({
        name: '',
        maxUsers: 1,
        validUntil: '',
        brand: '',
        deliveryNoteReaderType: '' as DeliveryNoteReaderType | '',
    });

    const fetchLicenses = async () => {
        setIsLoading(true);
        try {
            const raw = await listAllLicenses();
            setLicenses(raw.map(l => ({
                ...l,
                displayValidUntil: new Intl.DateTimeFormat('en-GB').format(new Date(l.validUntil)),
                displayBrand: BrandDescriptions[l.brand] || l.brand,
                displayDeliveryNoteReaderType: DeliveryNoteReaderTypeDescriptions[l.deliveryNoteReaderType] || l.deliveryNoteReaderType,
            })));
        } catch (error) {
            console.error('Failed to fetch licenses:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateLicense = async () => {
        if (!newLicense.name || !newLicense.maxUsers || !newLicense.validUntil || !newLicense.brand || !newLicense.deliveryNoteReaderType) {
            setErrorModal({ show: true, message: 'All fields are required.' }); return;
        }
        if (newLicense.name.length > 20) {
            setErrorModal({ show: true, message: 'Name cannot exceed 20 characters.' }); return;
        }
        if (newLicense.maxUsers < 1 || newLicense.maxUsers > 50) {
            setErrorModal({ show: true, message: 'Simultaneous Users must be between 1 and 50.' }); return;
        }
        if (isNaN(Date.parse(newLicense.validUntil))) {
            setErrorModal({ show: true, message: 'Invalid date format for Expires In.' }); return;
        }
        setLoadingCreate(true);
        try {
            await createLicense({
                name: newLicense.name,
                maxUsers: newLicense.maxUsers,
                validUntil: `${newLicense.validUntil}T23:59:59.00Z`,
                brand: newLicense.brand,
                deliveryNoteReaderType: newLicense.deliveryNoteReaderType as DeliveryNoteReaderType,
            } as LicenseCreateRequest);
            setShowCreateModal(false);
            fetchLicenses();
        } catch (error) {
            console.error('Failed to create license:', error);
            let errorMessage = 'An unexpected error occurred.';
            if (axios.isAxiosError(error)) errorMessage = error.response?.data?.detail || errorMessage;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingCreate(false);
        }
    };

    const handleEditLicense = async () => {
        if (!selectedLicense) return;
        if (selectedLicense.maxUsers < 1 || selectedLicense.maxUsers > 50) {
            setErrorModal({ show: true, message: 'Simultaneous Users must be between 1 and 50.' }); return;
        }
        if (isNaN(Date.parse(selectedLicense.validUntil))) {
            setErrorModal({ show: true, message: 'Invalid date format for Expires In.' }); return;
        }
        setLoadingEdit(true);
        try {
            await editLicense(selectedLicense.licenseHash, {
                maxUsers: selectedLicense.maxUsers,
                validUntil: `${selectedLicense.validUntil}T23:59:59.00Z`,
                deliveryNoteReaderType: selectedLicense.deliveryNoteReaderType,
            } as LicenseEditRequest);
            setShowEditModal(false);
            fetchLicenses();
        } catch (error) {
            console.error('Failed to edit license:', error);
            let errorMessage = 'An unexpected error occurred.';
            if (axios.isAxiosError(error)) errorMessage = error.response?.data?.detail || errorMessage;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingEdit(false);
        }
    };

    const columns = [
        { key: 'name', label: 'Name' },
        { key: 'displayBrand', label: 'Brand' },
        { key: 'displayDeliveryNoteReaderType', label: 'Delivery Note Reader' },
        { key: 'maxUsers', label: 'Simultaneous Users' },
        { key: 'displayValidUntil', label: 'Expires In' },
    ];

    const actions = [
        {
            label: 'Edit',
            className: 'bg-blue-50 text-blue-700 px-3 py-1 rounded-lg hover:bg-blue-100 transition text-sm',
            onClick: (license: License) => {
                setSelectedLicense({ ...license, validUntil: new Date(license.validUntil).toISOString().split('T')[0] });
                setShowEditModal(true);
            },
        },
    ];

    const openCreateModal = () => {
        setNewLicense({
            name: '',
            maxUsers: 1,
            validUntil: '', // Reset validUntil
            brand: '',
            deliveryNoteReaderType: '',
        });
        setShowCreateModal(true);
    };

    useEffect(() => { fetchLicenses(); }, []);

    return (
        <section id="licensesSection" className="dashboard-section">
            <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">Manage Licenses</h2>
                    <button className={`${btn.primary} flex items-center`} onClick={openCreateModal}>
                        <i className="fas fa-plus mr-2"></i> Add License
                    </button>
                </div>
                {isLoading ? <div className="text-center py-8 text-gray-400">Loading...</div> : <Table columns={columns} data={licenses} actions={actions} />}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className={overlay}>
                    <div className={modal}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-5">Create License</h3>
                        <form className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Name</label>
                                <input type="text" className={input} value={newLicense.name}
                                    onChange={e => setNewLicense({ ...newLicense, name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Simultaneous Users</label>
                                <input type="number" className={input} value={newLicense.maxUsers}
                                    onChange={e => setNewLicense({ ...newLicense, maxUsers: parseInt(e.target.value, 10) })} />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Expires In</label>
                                <input type="date" className={input} value={newLicense.validUntil}
                                    onChange={e => setNewLicense({ ...newLicense, validUntil: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Brand</label>
                                <select className={input} value={newLicense.brand}
                                    onChange={e => setNewLicense({ ...newLicense, brand: e.target.value as Brand })}>
                                    <option value="" disabled>Select a brand</option>
                                    {Object.values(Brand).map(b => <option key={b} value={b}>{BrandDescriptions[b]}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Delivery Note Reader</label>
                                <select className={input} value={newLicense.deliveryNoteReaderType}
                                    onChange={e => setNewLicense({ ...newLicense, deliveryNoteReaderType: e.target.value as DeliveryNoteReaderType })}>
                                    <option value="" disabled>Select a reader type</option>
                                    {Object.values(DeliveryNoteReaderType).map(type => (
                                        <option key={type} value={type}>{DeliveryNoteReaderTypeDescriptions[type]}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-between pt-2">
                                <button type="button" className={btn.secondary} onClick={() => setShowCreateModal(false)} disabled={loadingCreate}>Cancel</button>
                                <button type="button" className={btn.primary} onClick={handleCreateLicense} disabled={loadingCreate}>
                                    {loadingCreate ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedLicense && (
                <div className={overlay}>
                    <div className={modal}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-5">Edit License</h3>
                        <form className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Simultaneous Users</label>
                                <input type="number" className={input} value={selectedLicense.maxUsers}
                                    onChange={e => setSelectedLicense({ ...selectedLicense, maxUsers: parseInt(e.target.value, 10) })} />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Expires In</label>
                                <input type="date" className={input} value={selectedLicense.validUntil}
                                    onChange={e => setSelectedLicense({ ...selectedLicense, validUntil: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Delivery Note Reader</label>
                                <select className={input} value={selectedLicense.deliveryNoteReaderType}
                                    onChange={e => setSelectedLicense({ ...selectedLicense, deliveryNoteReaderType: e.target.value as DeliveryNoteReaderType })}>
                                    <option value="" disabled>Select a reader type</option>
                                    {Object.values(DeliveryNoteReaderType).map(type => (
                                        <option key={type} value={type}>{DeliveryNoteReaderTypeDescriptions[type]}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-between pt-2">
                                <button type="button" className={btn.secondary} onClick={() => setShowEditModal(false)} disabled={loadingEdit}>Cancel</button>
                                <button type="button" className={btn.primary} onClick={handleEditLicense} disabled={loadingEdit}>
                                    {loadingEdit ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {errorModal.show && (
                <div className={overlay}>
                    <div className={`${modal} text-center`}>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-exclamation text-red-500"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Error</h3>
                        <p className="text-gray-600 text-sm mb-5">{errorModal.message}</p>
                        <button className={btn.primary} onClick={() => setErrorModal({ show: false, message: '' })}>Close</button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default LicensesSection;
